/**
 * Business-rule rejections that must NOT page GlitchTip.
 *
 * The database guards reservation edits with `RAISE EXCEPTION`, which Hasura
 * surfaces as `constraint-violation` / "Check constraint violation. <sentence>".
 * Structurally that looks like a crash, but every message below is a decision
 * the product intends to make and already explains to the user as a toast
 * (`lib/notifications.ts`). Capturing them buries real bugs in expected noise
 * (#1397/#1398 for the deposit gate; the posted-night guards were still
 * paging as of the 2026-08-04 triage).
 *
 * This is a SUPPRESSION list, not error handling — the mutation still throws
 * and the caller still shows the toast. Only add a pattern here once the
 * message is genuinely user-actionable and genuinely surfaced in the UI.
 */

/**
 * Guards whose rejection is a product decision, not a fault.
 *
 * Sources: `taskyinn-backend/hasura/migrations` — the deposit gate on status
 * moves, and the night-audit guards that pin dates/rooms once nights have been
 * posted.
 */
const BUSINESS_VALIDATION_PATTERNS: readonly RegExp[] = [
  // Deposit gate: "Cannot move reservation X to status Y: requires N% paid, currently M%"
  /Cannot move reservation .+ requires [\d.]+% paid/i,
  // Night-audit guards: posted nights anchor check_in / room / room_type.
  /Cannot change check_in on reservation .+ posted night\(s\) exist/i,
  /Cannot shorten check_out on reservation .+ minimum allowed is/i,
  /Cannot change room\/room_type on reservation .+ posted night\(s\) already exist/i,
  // Lifecycle guards: the booking is cancelled/checked-out, so edit it via
  // restore or duplicate instead.
  /Cannot change check_in\/check_out on a .+ reservation/i,
  /Cannot change room\/room_type on a .+ reservation/i,
  /Cannot change checkout date after guest has been checked out/i,
  /Cannot change room on checked-in reservation .+ Use Split instead/i,
  // Status-automation rule rejection.
  /Transition from status .+ to .+ is not allowed/i,
  // Terminal lock (guard_reservation_terminal_lock): a checked-out / cancelled /
  // no-show stay is immutable except notes, and billing_company_id only after a
  // full credit note. The editor turns it into the locked-fields notice via
  // parseTerminalLockError. 210 events in GlitchTip #2218/#2219, every one a
  // person deliberately editing a locked field (2026-10-04 triage).
  /Cannot modify .+ on a (checked-out|cancelled|no-show) reservation/i,
  // Housekeeping front-desk guard (room_status_history_front_desk_guard): a
  // front-desk user flipping clean/dirty while the property setting is off.
  // The board shows it as the update-error toast (#3768). Old + new wording.
  /Housekeeping status changes are reserved for the housekeeping board/i,
  /Front-desk users cannot change a room's housekeeping status/i,
]

/** Whether a single GraphQL error message is an intended business rejection. */
export function isBusinessValidationMessage(message: string | undefined | null): boolean {
  if (!message) return false
  return BUSINESS_VALIDATION_PATTERNS.some((pattern) => pattern.test(message))
}

/**
 * Whether EVERY error on the response is an intended business rejection — the
 * condition for skipping capture. One unexplained error alongside them still
 * pages, so a real fault riding along with a rejection is never swallowed.
 */
export function isBusinessValidationOnly(
  errors: readonly { message?: string }[] | undefined | null
): boolean {
  if (!errors?.length) return false
  return errors.every((e) => isBusinessValidationMessage(e.message))
}
